import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { GameLogLeaderboardEntry } from '@/api/schemas';
import { fetchContestGameLogs } from '@/api/stats';
import { getAbbreviation } from '@/utils/teams';
import {
  CONTEST_SORT_OPTIONS,
  CONTEST_STAT_COLUMNS,
  CONTEST_SORTABLE_KEYS,
  COLUMN_TO_SORT_KEY,
  formatShortDate,
} from '@/utils/gameLogColumns';
import Spinner from '@/components/common/Spinner';

type PositionFilter = 'ALL' | 'FW' | 'MF' | 'DF' | 'GK';

const ROW_LIMIT = 500;
const REFETCH_INTERVAL_MS = 10000;

interface ContestGameLogsSectionProps {
  contestId: number;
  matchCount: number;
  isPastContest: boolean;
  token: string | null;
  onPlayerClick?: (entry: GameLogLeaderboardEntry) => void;
  onScoreClick?: (entry: GameLogLeaderboardEntry) => void;
}

const ContestGameLogsSection: React.FC<ContestGameLogsSectionProps> = ({
  contestId,
  matchCount,
  isPastContest,
  token,
  onPlayerClick,
  onScoreClick,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ sortBy: string; sortOrder: 'asc' | 'desc' }>({
    sortBy: 'fantasy_points',
    sortOrder: 'desc',
  });
  const [positionFilter, setPositionFilter] = useState<PositionFilter>('ALL');

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const { data: rows = [], error, isLoading, isFetching } = useQuery({
    queryKey: [
      'contestGameLogs',
      contestId,
      sortConfig.sortBy,
      sortConfig.sortOrder,
      positionFilter,
      debouncedSearchTerm,
    ],
    queryFn: () =>
      fetchContestGameLogs(
        contestId,
        {
          sort_by: sortConfig.sortBy,
          order: sortConfig.sortOrder,
          position: positionFilter === 'ALL' ? undefined : positionFilter,
          search: debouncedSearchTerm || undefined,
          limit: ROW_LIMIT,
        },
        token
      ),
    enabled: !!contestId,
    refetchOnWindowFocus: false,
    refetchInterval: isPastContest ? false : REFETCH_INTERVAL_MS,
    // Keep previous rows visible while filter/sort changes are fetching, so the
    // table doesn't flash to a spinner on every interaction.
    placeholderData: keepPreviousData,
  });

  const handleSortChange = (columnKey: string) => {
    const sortKey = COLUMN_TO_SORT_KEY[columnKey] || columnKey;
    if (sortConfig.sortBy === sortKey) {
      setSortConfig({ sortBy: sortKey, sortOrder: sortConfig.sortOrder === 'desc' ? 'asc' : 'desc' });
    } else {
      setSortConfig({ sortBy: sortKey, sortOrder: 'desc' });
    }
  };

  const hasActiveFilters = !!debouncedSearchTerm || positionFilter !== 'ALL';

  const emptyCopy = useMemo(() => {
    if (matchCount === 0) return 'No matches for this contest.';
    if (hasActiveFilters) return 'No players match your search.';
    return 'Player performances appear as matches play.';
  }, [matchCount, hasActiveFilters]);

  return (
    <div>
      {/* Filters */}
      <div className="px-3 py-2 border-b border-gray-700">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-1.5 bg-primary border border-gray-600 rounded text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value as PositionFilter)}
              className="px-3 py-1.5 bg-primary border border-gray-600 rounded text-sm text-gray-200 focus:outline-none focus:border-accent-yellow"
            >
              <option value="ALL">All Positions</option>
              <option value="FW">FW</option>
              <option value="MF">MF</option>
              <option value="DF">DF</option>
              <option value="GK">GK</option>
            </select>
            {/* Mobile sort dropdown */}
            <select
              value={sortConfig.sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="sm:hidden px-3 py-1.5 bg-primary border border-gray-600 rounded text-sm text-gray-200 focus:outline-none focus:border-accent-yellow"
            >
              <optgroup label="Performance">
                {CONTEST_SORT_OPTIONS.filter(opt => opt.category === 'performance').map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </optgroup>
              <optgroup label="Stats">
                {CONTEST_SORT_OPTIONS.filter(opt => opt.category === 'stats').map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </optgroup>
              <optgroup label="Other">
                {CONTEST_SORT_OPTIONS.filter(opt => opt.category === 'info').map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </optgroup>
            </select>
            <button
              onClick={() => setSortConfig(prev => ({ ...prev, sortOrder: prev.sortOrder === 'desc' ? 'asc' : 'desc' }))}
              className="sm:hidden px-3 py-1.5 bg-primary border border-gray-600 rounded text-sm text-gray-200 focus:outline-none focus:border-accent-yellow"
              title={sortConfig.sortOrder === 'desc' ? 'Switch to ascending' : 'Switch to descending'}
            >
              {sortConfig.sortOrder === 'desc' ? '▼' : '▲'}
            </button>
          </div>
        </div>
      </div>

      {/* Table body */}
      <div className="overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <Spinner size="md" />
            <span className="ml-3 text-gray-400 text-sm">Loading players...</span>
          </div>
        ) : error ? (
          <p className="text-center text-secondary italic p-8">
            Could not load player performances.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-center text-gray-500 italic py-12 px-4">{emptyCopy}</p>
        ) : (
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto scrollbar-mobile-hide">
            <table className="w-full text-left text-xs sm:text-sm">
              <thead className="sticky top-0 bg-surface-card z-10">
                <tr>
                  {CONTEST_STAT_COLUMNS.map(({ key, label, mobileHidden, align }) => {
                    const sortKey = COLUMN_TO_SORT_KEY[key] || key;
                    const isSortable = CONTEST_SORTABLE_KEYS.has(sortKey);
                    const isActive = sortConfig.sortBy === sortKey;
                    let thClasses = `p-2 font-semibold uppercase tracking-wider whitespace-nowrap text-${align ?? 'left'} select-none`;
                    thClasses += isActive ? ' text-accent-yellow' : ' text-gray-400';
                    if (isSortable) thClasses += ' cursor-pointer hover:text-gray-200';
                    if (mobileHidden) thClasses += ' hidden sm:table-cell';
                    if (key === 'player_name') thClasses += ' sticky top-0 left-0 bg-surface-card z-20 min-w-[120px]';
                    return (
                      <th
                        key={key}
                        className={thClasses}
                        onClick={isSortable ? () => handleSortChange(key) : undefined}
                        title={isSortable ? `Sort by ${label}` : undefined}
                      >
                        {label}
                        {isActive && (
                          <span className="ml-1 text-badge">
                            {sortConfig.sortOrder === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {rows.map((entry, idx) => (
                  <tr
                    key={`${entry.player_id}-${entry.match_id}-${idx}`}
                    className="cursor-pointer hover:bg-primary/40 active:bg-primary/40 transition-all duration-150"
                    onClick={onPlayerClick ? () => onPlayerClick(entry) : undefined}
                  >
                    {CONTEST_STAT_COLUMNS.map(({ key, mobileHidden, align }) => {
                      let value: string = '-';
                      let title: string | undefined = undefined;
                      const cellAlignmentClass = `text-${align ?? 'left'}`;
                      const cellHiddenClass = mobileHidden ? 'hidden sm:table-cell' : '';

                      if (key === 'player_name') {
                        value = entry.player_name;
                        title = value;
                      } else if (key === 'current_team_name') {
                        value = getAbbreviation(entry.current_team_name) || entry.current_team_name || '-';
                        title = entry.current_team_name ?? undefined;
                      } else if (key === 'primary_position') {
                        value = entry.primary_position ?? '-';
                      } else if (key === 'match_date') {
                        value = formatShortDate(entry.match_date);
                        title = entry.match_date;
                      } else if (key === 'opponent') {
                        value = entry.opponent ?? '-';
                      } else if (key === 'dfs_salary') {
                        value = entry.dfs_salary != null ? `$${entry.dfs_salary.toLocaleString()}` : '—';
                      } else if (key === 'fantasy_points') {
                        value = entry.fantasy_points.toFixed(1);
                      } else {
                        const numVal = entry[key as keyof GameLogLeaderboardEntry];
                        if (typeof numVal === 'number') {
                          value = String(numVal);
                        }
                      }

                      let tdClasses = `p-2 truncate ${cellHiddenClass} ${cellAlignmentClass}`;
                      if (key === 'player_name') {
                        tdClasses += ' sticky left-0 z-10 bg-surface-card font-semibold hover:text-accent-yellow hover:underline underline-offset-2';
                      } else if (key === 'fantasy_points') {
                        tdClasses += ' font-bold text-accent-yellow';
                        if (onScoreClick) tdClasses += ' cursor-pointer hover:underline underline-offset-2';
                      }

                      if (key === 'fantasy_points' && onScoreClick) {
                        return (
                          <td
                            key={key}
                            title={title}
                            className={tdClasses}
                            onClick={(e) => { e.stopPropagation(); onScoreClick(entry); }}
                          >
                            {value}
                          </td>
                        );
                      }

                      return (
                        <td key={key} title={title} className={tdClasses}>
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {isFetching && !isLoading && (
              <div className="text-center text-xs text-gray-500 py-2 border-t border-gray-700/50">
                Updating...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContestGameLogsSection;
